import type { ReactNode } from 'react';
import { Link } from 'react-router';

export interface Crumb {
  label: ReactNode;
  to?: string;
}

export function PageHeader({ title, subtitle, crumbs, actions, badges, icon }: { title: ReactNode; subtitle?: ReactNode; crumbs?: Crumb[]; actions?: ReactNode; badges?: ReactNode; icon?: ReactNode }) {
  return (
    <header className="mb-6 space-y-2">
      {crumbs && crumbs.length > 0 ? (
        <nav className="breadcrumbs py-0 text-sm text-base-content/60" aria-label="Breadcrumb">
          <ul>
            {crumbs.map((c, i) => (
              <li key={i}>
                {c.to ? (
                  <Link to={c.to} className="hover:text-base-content">
                    {c.label}
                  </Link>
                ) : (
                  <span aria-current="page" className="font-medium text-base-content">
                    {c.label}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </nav>
      ) : null}
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="flex min-w-0 items-start gap-3.5">
          {icon}
          <div className="min-w-0 space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="break-anywhere text-[1.625rem] leading-tight font-bold tracking-tight sm:text-[1.875rem]">{title}</h1>
              {badges}
            </div>
            {subtitle ? <div className="max-w-3xl text-base-content/65">{subtitle}</div> : null}
          </div>
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </header>
  );
}

/** A tinted icon chip for page headers and list rows. */
export function IconChip({ icon: Icon, tone = 'primary', size = 'md' }: { icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' }>; tone?: 'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'error' | 'neutral'; size?: 'sm' | 'md' | 'lg' }) {
  const box = size === 'lg' ? 'size-12 rounded-box' : size === 'sm' ? 'size-8 rounded-[0.6rem]' : 'size-10 rounded-field';
  const icon = size === 'lg' ? 'size-6' : size === 'sm' ? 'size-4' : 'size-5';
  return (
    <span className={`dr-tint-${tone} grid shrink-0 place-items-center ${box}`}>
      <Icon className={icon} aria-hidden="true" />
    </span>
  );
}

export function Section({ title, description, actions, children, className = '', id }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; id?: string }) {
  return (
    <section id={id} className={`card dr-surface min-w-0 ${className}`}>
      <div className="card-body gap-4 p-4 sm:p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className="card-title text-base font-semibold">{title}</h2>
            {description ? <div className="mt-0.5 text-sm text-base-content/60">{description}</div> : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
        {children}
      </div>
    </section>
  );
}
